import { useEffect, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { MotionBox } from '../../design/motion';
import { glass, color, ease, radius } from '../../design/tokens';

/** localStorage key read by analytics: 'accepted' or 'rejected'. Absent = not asked yet (no tracking). */
export const CONSENT_KEY = 'cookie';

function readConsent(): string | null {
    try { return localStorage.getItem(CONSENT_KEY); } catch { return 'unavailable'; }
}

function writeConsent(value: 'accepted' | 'rejected') {
    try { localStorage.setItem(CONSENT_KEY, value); } catch { /* storage blocked: banner just closes */ }
}

/**
 * Opt-in consent for analytics. The sign-in session cookie is strictly necessary and set regardless;
 * nothing else tracks a visitor until they choose "Allow analytics".
 */
export const CookieBanner = () => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!readConsent()) setShow(true);
    }, []);

    const choose = (value: 'accepted' | 'rejected') => {
        writeConsent(value);
        setShow(false);
    };

    return (
        <AnimatePresence>
            {show && (
                <MotionBox
                    role="dialog"
                    aria-label="Cookie preferences"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 24 }}
                    transition={{ duration: 0.6, ease: ease.out, delay: 0.8 }}
                    // Compact, so it covers as little as possible of a board's tools or a phone screen until answered
                    sx={{
                        position: 'fixed', zIndex: 1300,
                        left: { xs: 8, sm: 16 }, right: { xs: 8, sm: 'auto' }, bottom: { xs: 8, sm: 16 },
                        maxWidth: { sm: 520 },
                        px: 2, py: 1.25,
                        display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, rowGap: 0.5,
                        ...glass,
                        backgroundColor: 'rgba(14, 12, 38, 0.92)',
                        borderRadius: `${radius.md}px`,
                        boxShadow: `0 24px 60px -24px ${color.void}`,
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 220px', fontSize: 13, lineHeight: 1.4 }}>
                        A necessary cookie keeps you signed in; analytics only if you allow it.{' '}
                        <Link href="/privacy" color="inherit">Privacy</Link>
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
                        <Button size="small" onClick={() => choose('rejected')} sx={{ color: color.textMuted }}>Only necessary</Button>
                        <Button size="small" variant="contained" onClick={() => choose('accepted')}>Allow analytics</Button>
                    </Stack>
                </MotionBox>
            )}
        </AnimatePresence>
    );
};
